/* eslint-disable no-console */

/**
 * Local Storage Manager for Printer Settings
 * Manages local storage of settings, printers, and user preferences
 * kware: مدير التخزين المحلي للإعدادات والطابعات
 */
class LocalStorageManager {
  constructor() {
    this.storageKeys = {
      printers: 'folio-printers',
      settings: 'folio-print-settings',
      connectionHistory: 'folio-connection-history',
      downloadHistory: 'folio-download-history',
      zplTemplates: 'folio-zpl-templates',
      userPreferences: 'folio-user-preferences'
    };

    this.defaultSettings = {
      format: 'CODE128',
      layout: 'standard',
      includeText: true,
      copies: 1,
      paperSize: 'A4',
      orientation: 'portrait',
      printerMode: 'browser',
      printerType: 'ZEBRA_ZPL',
      templateId: 'zebra-standard',
      exportFormat: 'zpl',
      printSpeed: 6,
      printDensity: 15
    };

    this.defaultPreferences = {
      autoSavePrinters: true,
      showAdvancedSettings: false,
      rememberLastPrinter: true,
      enableMonitoring: true,
      monitoringInterval: 30000,
      theme: 'default',
      language: 'ar'
    };
  }

  /**
   * Save printer configuration
   */
  savePrinter(printer) {
    try {
      const printers = this.getAllPrinters();
      printers[printer.id] = {
        ...printer,
        saved: true,
        lastUsed: new Date().toISOString(),
        savedAt: new Date().toISOString()
      };

      localStorage.setItem(this.storageKeys.printers, JSON.stringify(printers));
      console.log(`Printer saved: ${printer.name}`);
      return true;
    } catch (error) {
      console.error('Failed to save printer:', error);
      return false;
    }
  }

  /**
   * Get all saved printers
   */
  getAllPrinters() {
    try {
      const stored = localStorage.getItem(this.storageKeys.printers);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to load printers:', error);
      return {};
    }
  }

  /**
   * Get printer by ID
   */
  getPrinter(printerId) {
    const printers = this.getAllPrinters();
    return printers[printerId] || null;
  }

  /**
   * Remove saved printer
   */
  removePrinter(printerId) {
    try {
      const printers = this.getAllPrinters();
      delete printers[printerId];
      localStorage.setItem(this.storageKeys.printers, JSON.stringify(printers));
      console.log(`Printer removed: ${printerId}`);
      return true;
    } catch (error) {
      console.error('Failed to remove printer:', error);
      return false;
    }
  }

  /**
   * Update printer last used time
   */
  updatePrinterUsage(printerId) {
    try {
      const printers = this.getAllPrinters();
      if (printers[printerId]) {
        printers[printerId].lastUsed = new Date().toISOString();
        localStorage.setItem(this.storageKeys.printers, JSON.stringify(printers));
      }
    } catch (error) {
      console.error('Failed to update printer usage:', error);
    }
  }

  /**
   * Save print settings
   */
  saveSettings(settings) {
    try {
      const settingsToSave = {
        ...this.defaultSettings,
        ...settings,
        lastSaved: new Date().toISOString()
      };

      localStorage.setItem(this.storageKeys.settings, JSON.stringify(settingsToSave));
      console.log('Print settings saved');
      return true;
    } catch (error) {
      console.error('Failed to save settings:', error);
      return false;
    }
  }

  /**
   * Get saved settings
   */
  getSettings() {
    try {
      const stored = localStorage.getItem(this.storageKeys.settings);
      if (stored) {
        const settings = JSON.parse(stored);
        // Merge with defaults to ensure all properties exist
        return { ...this.defaultSettings, ...settings };
      }
      return this.defaultSettings;
    } catch (error) {
      console.error('Failed to load settings:', error);
      return this.defaultSettings;
    }
  }

  /**
   * Reset settings to default
   */
  resetSettings() {
    try {
      localStorage.setItem(this.storageKeys.settings, JSON.stringify(this.defaultSettings));
      console.log('Settings reset to default');
      return this.defaultSettings;
    } catch (error) {
      console.error('Failed to reset settings:', error);
      return this.defaultSettings;
    }
  }

  /**
   * Save user preferences
   */
  savePreferences(preferences) {
    try {
      const prefsToSave = {
        ...this.defaultPreferences,
        ...preferences,
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem(this.storageKeys.userPreferences, JSON.stringify(prefsToSave));
      console.log('User preferences saved');
      return true;
    } catch (error) {
      console.error('Failed to save preferences:', error);
      return false;
    }
  }

  /**
   * Get user preferences
   */
  getPreferences() {
    try {
      const stored = localStorage.getItem(this.storageKeys.userPreferences);
      if (stored) {
        const preferences = JSON.parse(stored);
        return { ...this.defaultPreferences, ...preferences };
      }
      return this.defaultPreferences;
    } catch (error) {
      console.error('Failed to load preferences:', error);
      return this.defaultPreferences;
    }
  }

  /**
   * Save connection history
   */
  saveConnectionHistory(connection) {
    try {
      let history = this.getConnectionHistory();

      const historyEntry = {
        id: connection.id,
        name: connection.name,
        type: connection.type,
        connectedAt: connection.connectedAt,
        success: true
      };

      // Add to beginning of array
      history.unshift(historyEntry);

      // Keep only last 20 connections
      history = history.slice(0, 20);

      localStorage.setItem(this.storageKeys.connectionHistory, JSON.stringify(history));
      return true;
    } catch (error) {
      console.error('Failed to save connection history:', error);
      return false;
    }
  }

  /**
   * Get connection history
   */
  getConnectionHistory() {
    try {
      const stored = localStorage.getItem(this.storageKeys.connectionHistory);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load connection history:', error);
      return [];
    }
  }

  /**
   * Save download history
   */
  saveDownloadHistory(download) {
    try {
      let history = this.getDownloadHistory();

      const downloadEntry = {
        filename: download.filename,
        format: download.format,
        itemCount: download.metadata?.itemCount || 0,
        totalLabels: download.metadata?.totalLabels || 0,
        downloadedAt: new Date().toISOString(),
        size: download.metadata?.size || 0
      };

      history.unshift(downloadEntry);

      // Keep only last 50 downloads
      history = history.slice(0, 50);

      localStorage.setItem(this.storageKeys.downloadHistory, JSON.stringify(history));
      return true;
    } catch (error) {
      console.error('Failed to save download history:', error);
      return false;
    }
  }

  /**
   * Get download history
   */
  getDownloadHistory() {
    try {
      const stored = localStorage.getItem(this.storageKeys.downloadHistory);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load download history:', error);
      return [];
    }
  }

  /**
   * Save custom ZPL template
   */
  saveZPLTemplate(templateId, template) {
    try {
      const templates = this.getZPLTemplates();
      templates[templateId] = {
        ...template,
        id: templateId,
        createdAt: template.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem(this.storageKeys.zplTemplates, JSON.stringify(templates));
      console.log(`ZPL template saved: ${templateId}`);
      return true;
    } catch (error) {
      console.error('Failed to save ZPL template:', error);
      return false;
    }
  }

  /**
   * Get all ZPL templates
   */
  getZPLTemplates() {
    try {
      const stored = localStorage.getItem(this.storageKeys.zplTemplates);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to load ZPL templates:', error);
      return {};
    }
  }

  /**
   * Remove ZPL template
   */
  removeZPLTemplate(templateId) {
    try {
      const templates = this.getZPLTemplates();
      delete templates[templateId];
      localStorage.setItem(this.storageKeys.zplTemplates, JSON.stringify(templates));
      console.log(`ZPL template removed: ${templateId}`);
      return true;
    } catch (error) {
      console.error('Failed to remove ZPL template:', error);
      return false;
    }
  }

  /**
   * Get storage usage statistics
   */
  getStorageStats() {
    const stats = {
      printers: 0,
      settings: 0,
      connectionHistory: 0,
      downloadHistory: 0,
      zplTemplates: 0,
      userPreferences: 0,
      total: 0
    };

    try {
      Object.keys(this.storageKeys).forEach(key => {
        const item = localStorage.getItem(this.storageKeys[key]);
        if (item) {
          const size = new Blob([item]).size;
          stats[key] = size;
          stats.total += size;
        }
      });

      // Add human readable sizes
      stats.humanReadable = {
        printers: this.formatBytes(stats.printers),
        settings: this.formatBytes(stats.settings),
        connectionHistory: this.formatBytes(stats.connectionHistory),
        downloadHistory: this.formatBytes(stats.downloadHistory),
        zplTemplates: this.formatBytes(stats.zplTemplates),
        userPreferences: this.formatBytes(stats.userPreferences),
        total: this.formatBytes(stats.total)
      };

      return stats;
    } catch (error) {
      console.error('Failed to get storage stats:', error);
      return stats;
    }
  }

  /**
   * Format bytes to human readable
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Clear all stored data
   */
  clearAllData() {
    try {
      Object.values(this.storageKeys).forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('All stored data cleared');
      return true;
    } catch (error) {
      console.error('Failed to clear all data:', error);
      return false;
    }
  }

  /**
   * Export all data as JSON
   */
  exportData() {
    try {
      const exportData = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        data: {}
      };

      Object.keys(this.storageKeys).forEach(key => {
        const item = localStorage.getItem(this.storageKeys[key]);
        if (item) {
          try {
            exportData.data[key] = JSON.parse(item);
          } catch {
            exportData.data[key] = item;
          }
        }
      });

      return exportData;
    } catch (error) {
      console.error('Failed to export data:', error);
      return null;
    }
  }

  /**
   * Import data from JSON
   */
  importData(importData) {
    try {
      if (!importData || !importData.data) {
        throw new Error('Invalid import data');
      }

      let importedCount = 0;

      Object.keys(importData.data).forEach(key => {
        if (this.storageKeys[key]) {
          const dataToImport = typeof importData.data[key] === 'object'
            ? JSON.stringify(importData.data[key])
            : importData.data[key];

          localStorage.setItem(this.storageKeys[key], dataToImport);
          importedCount++;
        }
      });

      console.log(`Data imported: ${importedCount} items`);
      return { success: true, imported: importedCount };
    } catch (error) {
      console.error('Failed to import data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if localStorage is available
   */
  isAvailable() {
    try {
      const test = 'localStorage-test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get last used printer
   */
  getLastUsedPrinter() {
    const printers = this.getAllPrinters();
    const preferences = this.getPreferences();

    if (!preferences.rememberLastPrinter) {
      return null;
    }

    let lastPrinter = null;
    let lastUsedTime = 0;

    Object.values(printers).forEach(printer => {
      if (printer.lastUsed) {
        const usedTime = new Date(printer.lastUsed).getTime();
        if (usedTime > lastUsedTime) {
          lastUsedTime = usedTime;
          lastPrinter = printer;
        }
      }
    });

    return lastPrinter;
  }

  /**
   * Get frequently used printers
   */
  getFrequentPrinters(limit = 5) {
    const connectionHistory = this.getConnectionHistory();
    const printerCounts = {};

    // Count usage frequency
    connectionHistory.forEach(connection => {
      if (connection.success) {
        printerCounts[connection.id] = (printerCounts[connection.id] || 0) + 1;
      }
    });

    // Sort by frequency and return top printers
    return Object.keys(printerCounts)
      .map(id => ({
        printer: this.getPrinter(id),
        usage: printerCounts[id]
      }))
      .filter(item => item.printer)
      .sort((a, b) => b.usage - a.usage)
      .slice(0, limit);
  }
}

export default LocalStorageManager;
